import { ReactNode, forwardRef } from "react";

import { IconStateCheckmarkCircleOutline } from "@/assets/icons/figma/IconStateCheckmarkCircleOutline";
import { cn } from "@/common/utils/css.utils";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

export type TaskItem = {
  id: string;
  title: string;
  description: string | null;
  /** The one-line why under the description. */
  why: string | null;
  /** Plain-words reason lines for a rejected document. */
  reasonLines: string[];
};

type TaskChecklistProps = {
  id: string;
  title: string;
  tasks: TaskItem[];
  /** The bank account task, rendered under "On this page" with the form inside. */
  bankTask: {
    title: string;
    description: string | null;
    form: ReactNode;
  } | null;
  highlighted?: boolean;
};

const HIGHLIGHT_CLASS = "ring-2 ring-brand-500 ring-offset-4";

/**
 * A checklist without buttons: the page has exactly one primary button, in
 * the status card. The bank account task is the one exception, because it
 * is resolved here, with the IBAN form in place.
 */
export const TaskChecklist = forwardRef<HTMLElement, TaskChecklistProps>(
  ({ id, title, tasks, bankTask, highlighted = false }, ref) => {
    const { copy } = usePaymentCopy();

    if (tasks.length === 0 && !bankTask) {
      return null;
    }

    return (
      <section
        id={id}
        ref={ref}
        aria-labelledby={`${id}-title`}
        className={cn(
          "flex scroll-mt-24 flex-col gap-4 rounded-[0.25rem]",
          highlighted && HIGHLIGHT_CLASS
        )}
      >
        <Heading as="h2" size="h5" id={`${id}-title`}>
          {title}
        </Heading>
        {tasks.length > 0 && (
          <ul className="flex flex-col gap-4">
            {tasks.map(task => (
              <li key={task.id} className="flex gap-3" data-task={task.id}>
                <IconStateCheckmarkCircleOutline className="mt-0.5 size-6 shrink-0 text-grey-500" />
                <div className="flex min-w-0 flex-col gap-1">
                  <Body as="p" className="font-bold">
                    {task.title}
                  </Body>
                  {task.reasonLines.map(line => (
                    <Body as="p" key={line} className="text-attention-900">
                      {line}
                    </Body>
                  ))}
                  {task.description && <Body as="p">{task.description}</Body>}
                  {task.why && (
                    <Body as="p" color="muted" className="text-sm">
                      {task.why}
                    </Body>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {bankTask && (
          <div className="flex flex-col gap-3 border-t border-grey-200 pt-4">
            <Body
              as="p"
              color="muted"
              className="text-sm font-bold uppercase tracking-wide"
            >
              {copy("onThisPage")}
            </Body>
            <div className="flex gap-3">
              <IconStateCheckmarkCircleOutline className="mt-0.5 size-6 shrink-0 text-grey-500" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Body as="p" className="font-bold">
                  {bankTask.title}
                </Body>
                {bankTask.description && (
                  <Body as="p">{bankTask.description}</Body>
                )}
              </div>
            </div>
            <div className="md:pl-9">{bankTask.form}</div>
          </div>
        )}
      </section>
    );
  }
);

TaskChecklist.displayName = "TaskChecklist";
